import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ApiClientError,
  createComment,
  createReport,
  deleteComment,
  deletePost,
  fetchPostDetail,
  likeComment,
  likePost,
  type CommentItem,
  type PostItem,
} from '@suangua/shared';
import { useAuth } from '../lib/auth/AuthContext';
import { useUI } from '../lib/ui/UIContext';
import { MarkdownView } from '../components/board/MarkdownView';
import { AvatarSheet, EditPostModal, ReplyModal, ReportModal, type ReplyTarget, type ReportTarget } from '../components/board/DetailModals';
import { Avatar, BOARD_LABEL, fromNow, isOp, postTitle, verdictClass, verdictLabel } from '../components/ui/atoms';

/**
 * 帖子详情页（原型 post 屏复刻）：
 * 居中单列正文卡 + 楼中楼回复（两级，可展开/收起）+ 底部固定回复条
 * + 三弹层（回复编辑 / 举报 / 他人头像操作）。
 */

/** 点赞展示态（服务端数据 + 本地点赞覆盖） */
interface LikeState {
  count: number;
  liked: boolean;
}

export function PostDetailPage() {
  const { id } = useParams();
  const postId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useUI();

  const [post, setPost] = useState<PostItem | null>(null);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [postLike, setPostLike] = useState<LikeState>({ count: 0, liked: false });
  const [commentLikes, setCommentLikes] = useState<Record<number, LikeState>>({});
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // 弹层状态
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [avatarSheet, setAvatarSheet] = useState<{ name: string; url: string | null; userId: number } | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  /** 加载详情 + 评论 */
  const load = useCallback(async () => {
    try {
      const res = await fetchPostDetail(postId);
      setPost(res.post);
      setComments(res.comments);
      setPostLike({ count: res.post.like_count, liked: !!res.post.liked });
    } catch (err) {
      showToast(err instanceof ApiClientError ? err.message : '帖子不存在或已被删除');
    }
  }, [postId, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 回复总数（一级 + 楼中楼） */
  const replyCount = comments.reduce((n, c) => n + 1 + (c.children?.length ?? 0), 0);

  /** 帖子点赞（乐观更新，失败回滚） */
  async function togglePostLike() {
    if (!user) {
      showToast('请先登录后点赞');
      return;
    }
    const next = { count: postLike.count + (postLike.liked ? -1 : 1), liked: !postLike.liked };
    setPostLike(next);
    try {
      await likePost(postId, postLike.liked);
    } catch (err) {
      setPostLike(postLike); // 回滚
      showToast(err instanceof ApiClientError ? err.message : '点赞失败');
    }
  }

  /** 评论点赞 */
  async function toggleCommentLike(c: CommentItem) {
    if (!user) {
      showToast('请先登录后点赞');
      return;
    }
    const cur = commentLikes[c.id] ?? { count: c.like_count ?? 0, liked: !!c.liked };
    const next = { count: cur.count + (cur.liked ? -1 : 1), liked: !cur.liked };
    setCommentLikes((m) => ({ ...m, [c.id]: next }));
    try {
      await likeComment(c.id, cur.liked);
    } catch (err) {
      setCommentLikes((m) => ({ ...m, [c.id]: cur })); // 回滚
      showToast(err instanceof ApiClientError ? err.message : '点赞失败');
    }
  }

  /** 打开回复编辑器（root=null 回复帖子；root+child 楼中楼回复） */
  function openReply(root: CommentItem | null, child?: CommentItem) {
    if (!user) {
      showToast('请先登录后回复');
      return;
    }
    setReplyTarget(
      root
        ? {
            rootId: root.id,
            // 被回复的子评论 id（服务端据此写 reply_user_id）
            childId: child?.id,
            toName: child ? child.username : root.username,
            rootPreview: root.content,
            isChildReply: !!child,
          }
        : null,
    );
    setReplyOpen(true);
  }

  /**
   * 提交回复：parent_id 直传「被回复评论」——回复 L1 传其 id；回复 L2 传该 L2 id（服务端并入其父楼层
   * 并写 reply_user_id）。@昵称改由「头像、昵称」同行展示，不再前缀正文。
   */
  async function submitReply(content: string) {
    try {
      const parentId = replyTarget ? (replyTarget.childId ?? replyTarget.rootId) : null;
      await createComment(postId, parentId, content);
      await load();
      // 楼中楼回复后自动展开该层
      if (replyTarget) setExpanded((e) => ({ ...e, [replyTarget.rootId]: true }));
      showToast('回复发布成功');
    } catch (err) {
      showToast(err instanceof ApiClientError ? err.message : '回复失败');
    }
  }

  /** 提交举报（content = 帖子/评论；avatar = 头像） */
  async function submitReport(reason: string, note: string) {
    if (!reportTarget) return;
    const fullReason = note ? `${reason}：${note}` : reason;
    try {
      if (reportTarget.kind === 'avatar') {
        await createReport({ target_type: 'avatar', target_user_id: reportTarget.targetUserId, reason: fullReason });
      } else if (reportTarget.postId) {
        await createReport({ post_id: reportTarget.postId, reason: fullReason });
      } else if (reportTarget.commentId) {
        await createReport({ comment_id: reportTarget.commentId, reason: fullReason });
      }
      showToast('举报已提交，我们将在 24 小时内处理');
    } catch (err) {
      showToast(err instanceof ApiClientError ? err.message : '举报失败');
      throw err; // 保持弹层打开
    }
  }

  if (!post) {
    return (
      <div className="detail-wrap">
        <div className="empty-card">
          <i className="fa-regular fa-scroll" />
          <p>帖子加载中…</p>
        </div>
      </div>
    );
  }

  const isOwner = user?.id === post.user_id;
  const showVerdict = post.board === 'divination' && post.verdict;
  /** 隐藏帖（管理端处置）：仅作者/管理端可见，禁止回复与编辑 */
  const isHidden = post.is_hidden === 1;
  /** 只读或隐藏均视为不可回复 */
  const isLocked = post.is_locked === 1 || isHidden;

  return (
    <section>
      <div className="detail-wrap">
        {/* 返回 + 编辑/删除（作者本人；只读帖不可编辑） */}
        <div className="back-row">
          <button className="back-btn" onClick={() => navigate('/board')}>
            <i className="fa-solid fa-chevron-left" /> 返回列表
          </button>
          {isOwner && !isLocked && (
            <button className="back-btn txt-muted ml-auto" onClick={() => setEditOpen(true)}>
              <i className="fa-solid fa-pen" /> 编辑
            </button>
          )}
          {isOwner && (
            <button
              className={`back-btn txt-muted${!isLocked ? '' : ' ml-auto'}`}
              onClick={async () => {
                if (!window.confirm('确认删除这篇帖子？')) return;
                try {
                  await deletePost(postId);
                  showToast('帖子已删除');
                  navigate('/board');
                } catch (err) {
                  showToast(err instanceof ApiClientError ? err.message : '删除失败');
                }
              }}
            >
              <i className="fa-regular fa-trash-can" /> 删除
            </button>
          )}
        </div>

        {/* 隐藏帖提示（作者/管理端可见） */}
        {isHidden && (
          <p className="mb-3 flex items-center gap-2 rounded-lg border border-cinnabar-500/30 bg-cinnabar-500/10 px-4 py-2.5 text-[12px] text-cinnabar-500">
            <i className="fa-solid fa-eye-slash" />
            该帖子因违规已被管理员隐藏，仅你与管理员可见
          </p>
        )}

        {/* 正文卡 */}
        <article className="ink-card p-6">
          <div className="flex items-center gap-3">
            <Avatar
              name={post.username}
              url={post.avatar_url}
              size="sm"
              onClick={() =>
                post.user_id !== null && post.user_id !== user?.id &&
                setAvatarSheet({ name: post.username, url: post.avatar_url, userId: post.user_id })
              }
            />
            <div className="min-w-0 flex-1">
              <p className="flex items-baseline gap-1.5 text-[13px] leading-tight text-t1">
                {post.username}
                {/* UID 注册序号（公告帖无作者不展示） */}
                {post.uid != null && <span className="text-[10px] font-normal text-t3">UID · {post.uid}</span>}
              </p>
              <p className="mt-0.5 text-[11px] text-t2">
                {fromNow(post.created_at)} · {BOARD_LABEL[post.board]}
                {/* 发布后编辑过才展示编辑时间（updated_at 由编辑接口写入） */}
                {post.updated_at && <span> · 编辑于 {fromNow(post.updated_at)}</span>}
              </p>
            </div>
            {post.is_pinned === 1 && <span className="chip-xs text-cinnabar-500">置顶</span>}
            {post.is_locked === 1 && <span className="chip-xs text-t3">只读</span>}
            {isHidden && <span className="chip-xs text-cinnabar-500">已隐藏</span>}
            {showVerdict && (
              <span className={`verdict-seal ${verdictClass(post.verdict as string)}`}>
                {verdictLabel(post.verdict as string)}
              </span>
            )}
          </div>

          <h1 className="font-zh mt-4 text-2xl font-bold leading-snug text-t1">{postTitle(post)}</h1>
          <MarkdownView content={post.content} className="mt-3" />

          {/* 操作行（只读帖隐藏回复入口）；action-btn 提供悬浮反馈 */}
          <div className="mt-5 flex items-center gap-6 border-t border-line pt-4 text-[13px] text-t2">
            <button className={`action-btn flex items-center gap-2 ${postLike.liked ? 'txt-danger' : ''}`} onClick={() => void togglePostLike()}>
              <i className={postLike.liked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'} />
              {postLike.count}
            </button>
            {!isLocked && (
              <button className="action-btn flex items-center gap-2" onClick={() => openReply(null)}>
                <i className="fa-regular fa-comment" />
                {replyCount}
              </button>
            )}
            <button
              className="action-btn flex items-center gap-2"
              onClick={() => {
                void navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => showToast('链接已复制'))
                  .catch(() => showToast('复制失败'));
              }}
            >
              <i className="fa-solid fa-share-nodes" />
              分享
            </button>
            <button
              className="action-btn ml-auto flex items-center gap-2"
              onClick={() => setReportTarget({ kind: 'content', targetName: post.username, postId })}
            >
              <i className="fa-regular fa-flag" />
              举报
            </button>
          </div>
        </article>

        {/* 回复列表 */}
        <h2 className="section-title mt-6">回 复 {replyCount}</h2>
        {isLocked && (
          <p className="mb-3 rounded-lg border border-line bg-fill px-4 py-2.5 text-[12px] text-t2">
            <i className="fa-solid fa-lock mr-1.5" />
            该帖子已设为只读，暂停回复
          </p>
        )}
        <div className="ink-card px-6 py-2">
          {comments.length === 0 ? (
            <p className="py-10 text-center text-[13px] text-t3">
              {isLocked ? '暂无回复' : '还没有回复，来抢沙发'}
            </p>
          ) : (
            comments.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                opUserId={post.user_id}
                locked={isLocked}
                likeOf={(x) => commentLikes[x.id] ?? { count: x.like_count ?? 0, liked: !!x.liked }}
                expanded={!!expanded[c.id]}
                onToggleLike={(x) => void toggleCommentLike(x)}
                onReply={(child) => openReply(c, child)}
                onReport={(target) => setReportTarget(target)}
                onAvatar={(name, url, userId) => setAvatarSheet({ name, url, userId })}
                onDelete={async (cid) => {
                  try {
                    await deleteComment(cid);
                    await load();
                    showToast('回复已删除');
                  } catch (err) {
                    showToast(err instanceof ApiClientError ? err.message : '删除失败');
                  }
                }}
                onToggleExpand={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}
              />
            ))
          )}
        </div>
      </div>

      {/* 底部回复条（固定底部居中；只读帖不渲染） */}
      {!isLocked && (
        <div className="composer">
          <div className="composer-inner">
            <button className="flex-1 text-left text-[13px] text-t3" onClick={() => openReply(null)}>
              说点什么…
            </button>
            <button
              className={`composer-icon ${postLike.liked ? 'txt-danger' : ''}`}
              onClick={() => void togglePostLike()}
              aria-label="点赞"
            >
              <i className={postLike.liked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'} />
            </button>
            <button
              className="composer-icon"
              onClick={() => setReportTarget({ kind: 'content', targetName: post.username, postId })}
              aria-label="举报"
            >
              <i className="fa-regular fa-flag" />
            </button>
          </div>
        </div>
      )}

      {/* 四弹层 */}
      <ReplyModal
        open={replyOpen}
        postTitle={postTitle(post)}
        target={replyTarget}
        onClose={() => setReplyOpen(false)}
        onSubmit={submitReply}
      />
      <EditPostModal
        open={editOpen}
        post={post}
        onClose={() => setEditOpen(false)}
        onSaved={async () => {
          await load();
          showToast('帖子已更新');
        }}
        onError={(msg) => showToast(msg)}
      />
      <ReportModal
        open={!!reportTarget}
        target={reportTarget}
        onClose={() => setReportTarget(null)}
        onSubmit={submitReport}
        onError={(msg) => showToast(msg)}
      />
      <AvatarSheet
        open={!!avatarSheet}
        name={avatarSheet?.name ?? ''}
        url={avatarSheet?.url ?? null}
        onClose={() => setAvatarSheet(null)}
        onViewProfile={() => showToast('查看主页暂未开放')}
        onReportAvatar={() =>
          avatarSheet &&
          setReportTarget({ kind: 'avatar', targetName: avatarSheet.name, targetUserId: avatarSheet.userId })
        }
      />
    </section>
  );
}

/** 一级评论行（含楼中楼） */
function CommentRow(props: {
  comment: CommentItem;
  /** 帖子作者（楼主判定用；公告帖为 null） */
  opUserId: number | null;
  /** 帖子为只读态：隐藏一切回复入口 */
  locked: boolean;
  likeOf: (c: CommentItem) => LikeState;
  expanded: boolean;
  onToggleLike: (c: CommentItem) => void;
  onReply: (child?: CommentItem) => void;
  onReport: (target: ReportTarget) => void;
  onAvatar: (name: string, url: string | null, userId: number) => void;
  onDelete: (id: number) => Promise<void>;
  onToggleExpand: () => void;
}) {
  const {
    comment: c,
    opUserId,
    locked,
    likeOf,
    expanded,
    onToggleLike,
    onReply,
    onReport,
    onAvatar,
    onDelete,
    onToggleExpand,
  } = props;
  const { user } = useAuth();
  const like = likeOf(c);
  const children = c.children ?? [];
  // 收起时只展示第一条楼中楼（与原型一致）
  const visible = expanded ? children : children.slice(0, 1);

  return (
    <div className="reply-item">
      {/* 他人头像可点开操作弹层（查看主页/举报头像）；自己头像不触发 */}
      <Avatar
        name={c.username}
        url={c.avatar_url}
        size="sm"
        onClick={() => c.user_id !== user?.id && onAvatar(c.username, c.avatar_url, c.user_id)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[13px] text-t1">
            {c.username}
            {isOp(c, opUserId) && <span className="op-badge ml-1">楼主</span>}
          </p>
          <span className="ml-auto text-[11px] text-t3">{fromNow(c.created_at)}</span>
        </div>
        <MarkdownView content={c.content} className="mt-1" />

        <div className="reply-actions mt-2">
          <button className={like.liked ? 'liked' : ''} onClick={() => onToggleLike(c)}>
            <i className={like.liked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'} />
            {like.count}
          </button>
          {!locked && (
            <button onClick={() => onReply()}>
              <i className="fa-regular fa-comment" />
              回复
            </button>
          )}
          {c.user_id === user?.id && (
            <button onClick={() => void onDelete(c.id)}>
              <i className="fa-regular fa-trash-can" />
              删除
            </button>
          )}
          <button onClick={() => onReport({ kind: 'content', targetName: c.username, commentId: c.id })}>
            <i className="fa-regular fa-flag" />
            举报
          </button>
        </div>

        {/* 楼中楼 */}
        {children.length > 0 && (
          <div className="sub-replies">
            {visible.map((child) => (
              <ChildReply
                key={child.id}
                child={child}
                opUserId={opUserId}
                locked={locked}
                likeOf={likeOf}
                onToggleLike={onToggleLike}
                onReply={onReply}
                onReport={onReport}
                onAvatar={onAvatar}
                onDelete={onDelete}
              />
            ))}
            {children.length > 1 && (
              <button className="expand-btn" onClick={onToggleExpand}>
                {expanded ? '收起回复' : `展开 ${children.length - 1} 条回复`}
                <i className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** 楼中楼子回复行 */
function ChildReply(props: {
  child: CommentItem;
  opUserId: number | null;
  /** 帖子为只读态：隐藏回复入口 */
  locked: boolean;
  likeOf: (c: CommentItem) => LikeState;
  onToggleLike: (c: CommentItem) => void;
  onReply: (child?: CommentItem) => void;
  onReport: (target: ReportTarget) => void;
  onAvatar: (name: string, url: string | null, userId: number) => void;
  onDelete: (id: number) => Promise<void>;
}) {
  const { child, opUserId, locked, likeOf, onToggleLike, onReply, onReport, onAvatar, onDelete } = props;
  const { user } = useAuth();
  const like = likeOf(child);
  return (
    <div className="flex items-start gap-2 py-1.5">
      {/* 他人头像可点开操作弹层；自己头像不触发 */}
      <Avatar
        name={child.username}
        url={child.avatar_url}
        size="xs"
        onClick={() => child.user_id !== user?.id && onAvatar(child.username, child.avatar_url, child.user_id)}
      />
      <div className="min-w-0 flex-1">
        {/* 用户头像、昵称、@被回复昵称同行展示（0005 reply_user_id） */}
        <p className="flex items-center gap-1 text-[12px]">
          <span className="text-t1">
            {child.username}
            {isOp(child, opUserId) && <span className="op-badge ml-1">楼主</span>}
          </span>
          {child.reply_username && <span className="text-t3">@{child.reply_username}</span>}
        </p>
        <MarkdownView
          content={child.content.replace(/^【@[^】]*】\s*/, '')}
          className="mt-0.5 text-[12px]"
        />
        <div className="reply-actions mt-1">
          <button className={like.liked ? 'liked' : ''} onClick={() => onToggleLike(child)}>
            <i className={like.liked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'} />
            {like.count}
          </button>
          {!locked && (
            <button onClick={() => onReply(child)}>
              <i className="fa-regular fa-comment" />
              回复
            </button>
          )}
          {child.user_id === user?.id && (
            <button onClick={() => void onDelete(child.id)}>
              <i className="fa-regular fa-trash-can" />
              删除
            </button>
          )}
          <button onClick={() => onReport({ kind: 'content', targetName: child.username, commentId: child.id })}>
            <i className="fa-regular fa-flag" />
            举报
          </button>
        </div>
      </div>
    </div>
  );
}
